import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Star,
  Check,
  X,
  ExternalLink,
  HardHat,
  Loader2,
  AlertCircle,
  Minus,
  Plus,
  Search,
  GitCompare,
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { fetchToolsBySlugs, fetchToolFeaturesMany, fetchTools } from '../services/aecService';
import { generateToolComparison } from '../services/groqService';

// ── Constants ─────────────────────────────────────────────────
const PRICING_LABEL = {
  free: 'Free', freemium: 'Freemium', paid: 'One-time',
  subscription: 'Subscription', quote: 'Quote',
};
const PRICING_BADGE = {
  free:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  freemium:     'bg-teal-50 text-teal-700 border-teal-200',
  paid:         'bg-blue-50 text-blue-700 border-blue-200',
  subscription: 'bg-violet-50 text-violet-700 border-violet-200',
  quote:        'bg-amber-50 text-amber-700 border-amber-200',
};
const CATEGORY_ORDER = ['Core', 'BIM', 'Analysis', 'Visualisation', 'Collaboration', 'Construction', 'Platform', 'Integration', 'Advanced'];
const MAX_TOOLS = 3;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const isAvailable = (feat) => {
  if (!feat) return false;
  return feat.value_type === 'text' ? feat.value_text != null : feat.value_boolean === true;
};

// ── Cell renderers ────────────────────────────────────────────
const BoolCell = ({ value }) => {
  if (value == null) return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
  return value ? (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 mx-auto">
      <Check className="w-4 h-4 text-emerald-600" />
    </span>
  ) : (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-50 mx-auto">
      <X className="w-4 h-4 text-red-400" />
    </span>
  );
};

const FeatureCell = ({ feat }) => {
  if (!feat) return <BoolCell value={null} />;
  if (feat.value_type === 'text') {
    if (feat.value_text == null) return <BoolCell value={false} />;
    return (
      <span className="text-xs font-medium text-gray-700 text-center leading-snug px-1">
        {feat.value_text}
      </span>
    );
  }
  return <BoolCell value={feat.value_boolean} />;
};

const PillsCell = ({ values }) => {
  if (!values?.length) return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
  return (
    <div className="flex flex-wrap justify-center gap-1.5">
      {values.map((v) => (
        <span key={v} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-600">
          {capitalize(v)}
        </span>
      ))}
    </div>
  );
};

const RatingCell = ({ rating, count }) => {
  if (!rating) return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`w-3.5 h-3.5 ${
            n <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'
          }`} />
        ))}
      </div>
      <span className="text-xs text-gray-500">
        {Number(rating).toFixed(1)}
        {count > 0 && <span className="text-gray-400 ml-0.5">({count})</span>}
      </span>
    </div>
  );
};

const RECURRING_BILLING = new Set(['annual', 'yearly', 'monthly', 'quarterly', 'biannual']);
const resolveModel = (model, billing) =>
  model === 'paid' && RECURRING_BILLING.has(billing) ? 'subscription' : model;

const PricingCell = ({ model, billing }) => {
  if (!model) return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
  const m = resolveModel(model, billing);
  return (
    <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${
      PRICING_BADGE[m] ?? 'bg-gray-100 text-gray-600 border-gray-200'
    }`}>
      {PRICING_LABEL[m] ?? m}
    </span>
  );
};

const PriceCell = ({ price, currency, pricingModel }) => {
  const isFreeModel = pricingModel === 'free' || pricingModel === 'freemium';
  const isPaidModel = pricingModel && !isFreeModel;
  if (price > 0) return (
    <span className="text-sm text-gray-900 font-medium">
      {currency ?? 'USD'} {Number(price).toLocaleString()}
      <span className="text-gray-400 font-normal">/yr</span>
    </span>
  );
  if (isFreeModel) return <span className="text-sm text-emerald-600 font-medium">Free</span>;
  if (isPaidModel) return <span className="text-sm text-amber-700 font-medium">Contact Sales</span>;
  return <span className="text-sm text-gray-400">—</span>;
};

// ── Table layout primitives ───────────────────────────────────
const SectionHeading = ({ title, badge, badgeStyle }) => (
  <div style={{ gridColumn: '1 / -1' }}
    className="px-6 pt-5 pb-2 bg-gray-50 border-t border-b border-gray-100 flex items-center gap-2">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{title}</p>
    {badge != null && (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badgeStyle}`}>
        {badge}
      </span>
    )}
  </div>
);

const CategoryDivider = ({ label, templateCols }) => (
  <div
    className="col-span-full grid border-t border-gray-100 bg-gray-50/40"
    style={{ gridTemplateColumns: templateCols }}
  >
    <div className="px-6 py-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</span>
    </div>
  </div>
);

const Row = ({ label, cells, shade }) => (
  <>
    <div className={`px-6 py-3.5 flex items-center text-sm text-gray-500 border-t border-gray-100 ${shade ? 'bg-gray-50/60' : 'bg-white'}`}>
      {label}
    </div>
    {cells.map((cell, i) => (
      <div key={i}
        className={`px-4 py-3.5 flex items-center justify-center border-t border-l border-gray-100 ${shade ? 'bg-gray-50/60' : 'bg-white'}`}>
        {cell}
      </div>
    ))}
  </>
);

// ── Tool column header — with remove button ───────────────────
const ToolHeader = ({ tool, onRemove }) => (
  <div className="relative flex flex-col items-center gap-3 px-4 py-6 border-l border-gray-100">
    <button
      onClick={() => onRemove(tool.slug)}
      title="Remove from comparison"
      className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center
        bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
    >
      <X className="w-3.5 h-3.5" />
    </button>
    <Link
      to={`/library/aec/${tool.slug}`}
      title={`View ${tool.name} details`}
      className="w-14 h-14 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-violet-300 hover:border-violet-300 transition-all"
    >
      {tool.logo_url
        ? <img src={tool.logo_url} alt={tool.name} className="w-full h-full object-contain p-2" />
        : <HardHat className="w-7 h-7 text-gray-400" />
      }
    </Link>
    <div className="text-center">
      <Link
        to={`/library/aec/${tool.slug}`}
        className="text-sm font-semibold text-gray-900 leading-snug hover:text-violet-700 transition-colors"
      >
        {tool.name}
      </Link>
      <p className="text-xs text-gray-400 mt-0.5">{tool.vendor_name}</p>
    </div>
    {tool.website_url && (
      <a href={tool.affiliate_url ?? tool.website_url}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-gray-900 hover:bg-gray-700 text-white transition-colors">
        Visit site <ExternalLink className="w-3 h-3" />
      </a>
    )}
  </div>
);

// ── Tool search popover ───────────────────────────────────────
const ToolSearchSlot = ({ onAdd, excludeSlugs }) => {
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const ref                     = useRef(null);
  const inputRef                = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus input when popover opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      fetchTools({ search: query })
        .then((data) =>
          setResults(data.filter((t) => !excludeSlugs.includes(t.slug)).slice(0, 8))
        )
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 280);
    return () => clearTimeout(timer);
  }, [query, excludeSlugs]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex flex-col items-center justify-center gap-2 w-full h-full min-h-[140px] rounded-2xl border-2 border-dashed border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50/30 transition-all group"
      >
        <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
          <Plus className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
        </div>
        <p className="text-sm font-medium text-gray-400 group-hover:text-blue-500 transition-colors">Add a tool</p>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-30 w-72 rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by tool or vendor name…"
              className="flex-1 text-sm text-gray-900 placeholder-gray-400 bg-transparent outline-none"
            />
            {searching && <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin shrink-0" />}
          </div>

          {/* Results */}
          <div className="max-h-64 overflow-y-auto py-1">
            {!query.trim() && (
              <p className="text-xs text-gray-400 text-center py-6 px-4">Type to search tools…</p>
            )}
            {query.trim() && !searching && results.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6 px-4">No tools found for "{query}"</p>
            )}
            {results.map((t) => (
              <button
                key={t.slug}
                onClick={() => { onAdd(t.slug); setOpen(false); setQuery(''); setResults([]); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
                  {t.logo_url
                    ? <img src={t.logo_url} alt={t.name} className="w-full h-full object-contain p-1" />
                    : <HardHat className="w-4 h-4 text-gray-400" />
                  }
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                  <p className="text-xs text-gray-400 truncate">{t.vendor_name}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Compare selector bar (top of page) ───────────────────────
const CompareSelector = ({ tools, slugs, onAdd, onRemove }) => {
  const emptySlots = MAX_TOOLS - slugs.length;

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Select tools to compare ({slugs.length}/{MAX_TOOLS})
        </p>
        <div className={`grid gap-3 ${
          slugs.length + emptySlots === 2 ? 'grid-cols-2' : 'grid-cols-3'
        }`}>
          {/* Filled slots */}
          {tools.map((tool) => (
            <div key={tool.slug}
              className="relative flex items-center gap-3 px-4 py-3 rounded-2xl border-2 border-blue-200 bg-blue-50/40">
              <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
                {tool.logo_url
                  ? <img src={tool.logo_url} alt={tool.name} className="w-full h-full object-contain p-1" />
                  : <HardHat className="w-4 h-4 text-gray-400" />
                }
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{tool.name}</p>
                <p className="text-xs text-gray-400 truncate">{tool.vendor_name}</p>
              </div>
              <button
                onClick={() => onRemove(tool.slug)}
                title="Remove"
                className="w-6 h-6 rounded-full flex items-center justify-center bg-white border border-gray-200 hover:bg-red-100 hover:border-red-200 text-gray-400 hover:text-red-500 transition-colors shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <ToolSearchSlot
              key={`empty-${i}`}
              onAdd={onAdd}
              excludeSlugs={slugs}
            />
          ))}
        </div>

        {slugs.length >= 2 && (
          <p className="text-xs text-gray-400 mt-3 text-center">
            Comparing {slugs.length} tools — scroll down to see the table
          </p>
        )}
        {slugs.length < 2 && (
          <p className="text-xs text-gray-400 mt-3 text-center">
            Add {2 - slugs.length} more tool{2 - slugs.length > 1 ? 's' : ''} to start comparing
          </p>
        )}
      </div>
    </div>
  );
};

// ── Inline AI cell helpers ────────────────────────────────────
const TextCell = ({ text }) =>
  text
    ? <p className="text-xs text-gray-600 leading-relaxed">{text}</p>
    : <Minus className="w-4 h-4 text-gray-300 mx-auto" />;

const ListCell = ({ items }) =>
  items?.length
    ? <ul className="space-y-1 text-left">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
            <span className="mt-1 w-1 h-1 rounded-full bg-violet-300 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    : <Minus className="w-4 h-4 text-gray-300 mx-auto" />;

const TopFeaturesCell = ({ features }) =>
  features?.length
    ? <div className="space-y-2 text-left">
        {features.map((f, i) => (
          <div key={i}>
            <p className="text-xs font-semibold text-gray-800">{f.name}</p>
            <p className="text-[11px] text-gray-500 leading-relaxed">{f.description}</p>
          </div>
        ))}
      </div>
    : <Minus className="w-4 h-4 text-gray-300 mx-auto" />;

// ── Main page ─────────────────────────────────────────────────
const AECCompare = () => {
  const { slugs: slugParam } = useParams();
  const navigate = useNavigate();

  const slugs = useMemo(
    () => (slugParam ?? '').split('-vs-').map((s) => s.trim()).filter(Boolean).slice(0, MAX_TOOLS),
    [slugParam]
  );

  const [tools, setTools]                   = useState([]);
  const [featuresMap, setFeaturesMap]       = useState({});
  const [toolsLoading, setToolsLoading]     = useState(false);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [error, setError]                   = useState(null);

  const [aiInsights, setAiInsights]         = useState(null);
  const [aiLoading, setAiLoading]           = useState(false);
  const [aiError, setAiError]               = useState(null);
  const aiAutoRunRef                        = useRef(null);

  const [showFeatures, setShowFeatures]     = useState(false);

  // ── Helpers to add/remove tools (updates URL) ─────────────
  const addTool = useCallback((slug) => {
    if (slugs.length >= MAX_TOOLS || slugs.includes(slug)) return;
    const next = [...slugs, slug];
    navigate(`/library/aec/compare/${next.join('-vs-')}`, { replace: true });
  }, [slugs, navigate]);

  const removeTool = useCallback((slug) => {
    const next = slugs.filter((s) => s !== slug);
    if (next.length === 0) navigate('/library/aec/compare', { replace: true });
    else navigate(`/library/aec/compare/${next.join('-vs-')}`, { replace: true });
  }, [slugs, navigate]);

  // ── Load tools when slug list changes ─────────────────────
  useEffect(() => {
    if (!slugs.length) { setTools([]); setFeaturesMap({}); return; }
    setToolsLoading(true);
    setError(null);
    setAiInsights(null);
    setAiError(null);
    aiAutoRunRef.current = null;
    fetchToolsBySlugs(slugs)
      .then(setTools)
      .catch(() => setError('Failed to load one or more tools.'))
      .finally(() => setToolsLoading(false));
  }, [slugParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load features once tools are resolved ─────────────────
  useEffect(() => {
    if (!tools.length) { setFeaturesMap({}); return; }
    setFeaturesLoading(true);
    fetchToolFeaturesMany(tools.map((t) => t.id))
      .then(setFeaturesMap)
      .catch(console.error)
      .finally(() => setFeaturesLoading(false));
  }, [tools]);

  // ── AI: run once per unique tool set, after features load ──
  const runAI = useCallback(() => {
    if (tools.length < 2) return;
    setAiLoading(true);
    setAiError(null);
    generateToolComparison({ tools, featuresMap })
      .then((data) => { setAiInsights(data); setAiLoading(false); })
      .catch((err) => { setAiError(err.message ?? 'AI analysis failed'); setAiLoading(false); });
  }, [tools, featuresMap]);

  useEffect(() => {
    if (featuresLoading || tools.length < 2) return;
    const key = [...slugs].sort().join('-vs-');
    if (aiAutoRunRef.current === key) return;
    aiAutoRunRef.current = key;
    runAI();
  }, [featuresLoading, tools, slugs, runAI]);

  // ── Feature comparison data ───────────────────────────────
  const allFeatures = useMemo(() => {
    const bySlug = {};
    for (const feats of Object.values(featuresMap)) {
      for (const f of feats) {
        if (!bySlug[f.feature_slug]) {
          bySlug[f.feature_slug] = {
            slug:       f.feature_slug,
            name:       f.feature_name,
            category:   f.feature_category ?? 'Other',
            sort_order: f.sort_order ?? 999,
            value_type: f.value_type,
          };
        }
      }
    }
    return Object.values(bySlug).sort((a, b) => a.sort_order - b.sort_order);
  }, [featuresMap]);

  const getFeat = (featureSlug, toolId) =>
    featuresMap[toolId]?.find((f) => f.feature_slug === featureSlug) ?? null;

  const { sharedFeatures, diffFeatures } = useMemo(() => {
    const active = allFeatures.filter((f) =>
      tools.some((t) => getFeat(f.slug, t.id) != null)
    );
    const shared = active.filter((f) => tools.every((t) => isAvailable(getFeat(f.slug, t.id))));
    const diff   = active.filter((f) => !tools.every((t) => isAvailable(getFeat(f.slug, t.id))));
    return { sharedFeatures: shared, diffFeatures: diff };
  }, [allFeatures, tools, featuresMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupByCategory = (features) => {
    const map = {};
    for (const f of features) {
      (map[f.category] = map[f.category] ?? []).push(f);
    }
    const known   = CATEGORY_ORDER.filter((c) => map[c]);
    const unknown = Object.keys(map).filter((c) => !CATEGORY_ORDER.includes(c)).sort();
    return [...known, ...unknown].map((cat) => ({ cat, features: map[cat] }));
  };

  const sharedGroups = useMemo(() => groupByCategory(sharedFeatures), [sharedFeatures]);
  const diffGroups   = useMemo(() => groupByCategory(diffFeatures),   [diffFeatures]);

  const colCount     = tools.length;
  const templateCols = colCount === 3 ? '200px 1fr 1fr 1fr' : colCount === 2 ? '200px 1fr 1fr' : '200px 1fr';

  const showTable = tools.length >= 2 && !error;

  const overviewRows = [
    { label: 'Rating',         cells: tools.map((t) => <RatingCell rating={t.avg_rating} count={t.review_count} />) },
    { label: 'Pricing model',  cells: tools.map((t) => <PricingCell model={t.pricing_model} billing={t.billing_cycle} />) },
    { label: 'Starting price', cells: tools.map((t) => <PriceCell price={t.price_from} currency={t.price_currency} pricingModel={t.pricing_model} />) },
    { label: 'Free trial',     cells: tools.map((t) => <BoolCell value={t.has_free_trial} />) },
    { label: 'Platforms',      cells: tools.map((t) => <PillsCell values={t.platforms} />) },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Top nav ── */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link to="/library/aec"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium">
            <ArrowLeft className="w-4 h-4" /> AEC Tools
          </Link>
          <span className="text-gray-300">/</span>
          <div className="flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-gray-400" />
            <p className="text-sm text-gray-900 font-medium">Compare Tools</p>
          </div>
          {colCount >= 2 && (
            <p className="ml-auto text-xs text-gray-400 hidden sm:block">
              {tools.map((t) => t.name).join(' · ')}
            </p>
          )}
        </div>
      </header>

      {/* ── Tool selector ── */}
      <CompareSelector
        tools={tools}
        slugs={slugs}
        onAdd={addTool}
        onRemove={removeTool}
      />

      {/* ── Loading state ── */}
      {toolsLoading && (
        <div className="flex items-center justify-center gap-2 py-20 text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading tools…
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="flex flex-col items-center gap-3 py-20 text-center px-6">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <p className="text-sm text-red-600 font-medium">{error}</p>
        </div>
      )}

      {/* ── Empty / not enough tools ── */}
      {!toolsLoading && !error && slugs.length < 2 && (
        <div className="flex flex-col items-center justify-center py-24 text-center px-6">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
            <GitCompare className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-base font-semibold text-gray-900 mb-1">
            {slugs.length === 0 ? 'Select tools above to compare' : 'Add one more tool to compare'}
          </p>
          <p className="text-sm text-gray-400 max-w-xs">
            {slugs.length === 0
              ? 'Use the search slots above to find and add up to 3 tools.'
              : 'You need at least 2 tools to generate a side-by-side comparison.'}
          </p>
        </div>
      )}

      {/* ── Comparison table ── */}
      {showTable && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="grid overflow-x-auto" style={{ gridTemplateColumns: templateCols }}>

              {/* Sticky tool header row */}
              <div className="col-span-full grid sticky top-14 z-20 bg-white/90 backdrop-blur-xl border-b border-gray-200"
                style={{ gridTemplateColumns: templateCols }}>
                <div className="px-6 py-5 flex items-end">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">{colCount} tools</p>
                </div>
                {tools.map((tool) => (
                  <ToolHeader key={tool.id} tool={tool} onRemove={removeTool} />
                ))}
              </div>

              {/* ── Overview ── */}
              <SectionHeading title="Overview" />
              <Row
                label="Description"
                cells={tools.map((t) => (
                  <p key={t.id} className="text-xs text-gray-600 leading-relaxed text-left">
                    {t.description_short ?? t.tagline ?? '—'}
                  </p>
                ))}
                shade={false}
              />
              {overviewRows.map((row, i) => (
                <Row key={row.label} label={row.label} cells={row.cells} shade={(i + 1) % 2 !== 0} />
              ))}

              {/* ── AI Quick Facts ── */}
              <div style={{ gridColumn: '1 / -1' }}
                className="px-6 pt-5 pb-2 bg-gray-50 border-t border-b border-gray-100 flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-violet-100">
                  <Sparkles className="w-3 h-3 text-violet-600" />
                </span>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">AI Quick Facts</p>
                <span className="text-[10px] text-gray-400">powered by Groq</span>
                {aiLoading && <Loader2 className="w-3 h-3 text-violet-400 animate-spin ml-1" />}
                {!aiLoading && (aiInsights || aiError) && (
                  <button
                    onClick={runAI}
                    className="ml-auto flex items-center gap-1 text-[10px] text-gray-400 hover:text-violet-600 transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                )}
              </div>

              {/* AI skeleton rows */}
              {aiLoading && [0, 1, 2, 3].map((i) => (
                <Row
                  key={`ai-sk-${i}`}
                  label={<div className="h-2.5 w-20 rounded-full bg-gray-100 animate-pulse" />}
                  cells={tools.map((t) => (
                    <div key={t.id} className="w-full space-y-1.5">
                      <div className="h-2 rounded-full bg-gray-100 animate-pulse w-full" />
                      <div className="h-2 rounded-full bg-gray-100 animate-pulse w-3/4" />
                    </div>
                  ))}
                  shade={i % 2 !== 0}
                />
              ))}

              {/* AI error */}
              {!aiLoading && aiError && (
                <div style={{ gridColumn: '1 / -1' }}
                  className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-amber-50/60">
                  <div className="flex items-center gap-2 text-xs text-amber-700">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    {aiError}
                  </div>
                  <button
                    onClick={runAI}
                    className="flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 transition-colors shrink-0"
                  >
                    <RefreshCw className="w-3 h-3" /> Retry
                  </button>
                </div>
              )}

              {/* AI data rows */}
              {!aiLoading && aiInsights && (() => {
                const getAI = (tool) => aiInsights.tools?.find((ti) => ti.slug === tool.slug) ?? null;
                return [
                  { label: 'Popular for',    cells: tools.map((t) => <TextCell key={t.id} text={getAI(t)?.popular_for} />) },
                  { label: 'Best for',       cells: tools.map((t) => <TextCell key={t.id} text={getAI(t)?.best_for} />) },
                  { label: 'Roles',          cells: tools.map((t) => <PillsCell key={t.id} values={getAI(t)?.best_roles} />) },
                  { label: 'Disciplines',    cells: tools.map((t) => <PillsCell key={t.id} values={getAI(t)?.best_disciplines} />) },
                  { label: 'Project stages', cells: tools.map((t) => <PillsCell key={t.id} values={getAI(t)?.best_stages} />) },
                  { label: 'Top use cases',  cells: tools.map((t) => <ListCell key={t.id} items={getAI(t)?.top_usecases} />) },
                  { label: 'Top features',   cells: tools.map((t) => <TopFeaturesCell key={t.id} features={getAI(t)?.top_features} />) },
                ].map((row, i) => (
                  <Row key={row.label} label={row.label} cells={row.cells} shade={i % 2 !== 0} />
                ));
              })()}

              {/* AI key differences + recommendations */}
              {!aiLoading && aiInsights?.comparison?.key_differences?.length > 0 && (
                <>
                  <div className="px-6 py-3.5 flex items-start text-sm text-gray-500 border-t border-gray-100 bg-white">
                    Key differences
                  </div>
                  <div style={{ gridColumn: `2 / -1` }}
                    className="px-5 py-3.5 border-t border-l border-gray-100 bg-white">
                    <ul className="space-y-1.5">
                      {aiInsights.comparison.key_differences.map((d, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {!aiLoading && aiInsights?.comparison?.recommendation && (
                <div style={{ gridColumn: '1 / -1' }}
                  className="border-t border-gray-100 px-6 py-5 bg-white">
                  <p className="text-xs font-semibold text-gray-500 mb-3">Recommended by team size</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'individual', label: 'Individual' },
                      { key: 'small_team',  label: 'Small Team' },
                      { key: 'enterprise',  label: 'Enterprise' },
                    ].map(({ key, label }) =>
                      aiInsights.comparison.recommendation[key] ? (
                        <div key={key} className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                          <p className="text-xs text-gray-700 leading-relaxed">{aiInsights.comparison.recommendation[key]}</p>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              )}

              {/* ── Feature Comparison (collapsible) ── */}
              <div style={{ gridColumn: '1 / -1' }}
                className="px-6 pt-5 pb-2 bg-gray-50 border-t border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Feature Comparison</p>
                  {(sharedFeatures.length + diffFeatures.length) > 0 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-500">
                      {sharedFeatures.length + diffFeatures.length}
                    </span>
                  )}
                  {featuresLoading && <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />}
                </div>
                <button
                  onClick={() => setShowFeatures((v) => !v)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors"
                >
                  {showFeatures
                    ? <><ChevronUp className="w-3.5 h-3.5" /> Collapse</>
                    : <><ChevronDown className="w-3.5 h-3.5" /> Expand</>
                  }
                </button>
              </div>

              {showFeatures && (
                <>
                  {featuresLoading && (
                    <div style={{ gridColumn: '1 / -1' }}
                      className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400 border-t border-gray-100">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading feature data…
                    </div>
                  )}

                  {!featuresLoading && sharedFeatures.length > 0 && (
                    <>
                      <SectionHeading
                        title="Shared Features"
                        badge={sharedFeatures.length}
                        badgeStyle="bg-emerald-100 text-emerald-700"
                      />
                      {sharedGroups.map(({ cat, features }) => (
                        <div key={cat} className="col-span-full grid"
                          style={{ gridTemplateColumns: templateCols }}>
                          <CategoryDivider label={cat} templateCols={templateCols} />
                          {features.map((f, i) => (
                            <Row
                              key={f.slug}
                              label={f.name}
                              cells={tools.map((t) => <FeatureCell key={t.id} feat={getFeat(f.slug, t.id)} />)}
                              shade={i % 2 !== 0}
                            />
                          ))}
                        </div>
                      ))}
                    </>
                  )}

                  {!featuresLoading && diffFeatures.length > 0 && (
                    <>
                      <SectionHeading
                        title="Where They Differ"
                        badge={diffFeatures.length}
                        badgeStyle="bg-amber-100 text-amber-700"
                      />
                      {diffGroups.map(({ cat, features }) => (
                        <div key={cat} className="col-span-full grid"
                          style={{ gridTemplateColumns: templateCols }}>
                          <CategoryDivider label={cat} templateCols={templateCols} />
                          {features.map((f, i) => (
                            <Row
                              key={f.slug}
                              label={f.name}
                              cells={tools.map((t) => <FeatureCell key={t.id} feat={getFeat(f.slug, t.id)} />)}
                              shade={i % 2 !== 0}
                            />
                          ))}
                        </div>
                      ))}
                    </>
                  )}

                  {!featuresLoading && sharedFeatures.length === 0 && diffFeatures.length === 0 && (
                    <div style={{ gridColumn: '1 / -1' }}
                      className="flex flex-col items-center gap-2 py-12 text-center border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-500">No feature data yet</p>
                      <p className="text-xs text-gray-400 max-w-xs">
                        Seed tool_features rows for these tools to enable feature comparison.
                      </p>
                    </div>
                  )}
                </>
              )}

              

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AECCompare;
